import { useMemo } from 'react';

import type { SelectOptionT } from '@/components/Select/types';
import { COMPANY_STATUS_MAP } from '@/features/Account/types/constants';
import useAgentStore from '@/features/Agent/stores';
import useLocaleStore from '@/stores/LocaleStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';

const createOptionsFromValues = (values: string[]): SelectOptionT[] => {
  return values.map((value) => ({
    value,
    searchName: value,
  }));
};

const useCompanyAttributes = () => {
  const workspaceFieldOptions = useWorkspaceStore(
    (state) => state.workspaceFieldOptions,
  );
  const regionMap = useLocaleStore((state) => state.regionMap);
  const agentMap = useAgentStore((state) => state.agentMap);

  const industryOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(workspaceFieldOptions.industry_values);
  }, [workspaceFieldOptions.industry_values]);

  const sizeOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(workspaceFieldOptions.size_values);
  }, [workspaceFieldOptions.size_values]);

  const planOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(workspaceFieldOptions.plan_values);
  }, [workspaceFieldOptions.plan_values]);

  const ratingOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(workspaceFieldOptions.rating_values);
  }, [workspaceFieldOptions.rating_values]);

  const accountTypeOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(workspaceFieldOptions.account_type_values);
  }, [workspaceFieldOptions.account_type_values]);

  const accountStageOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(workspaceFieldOptions.account_stage_values);
  }, [workspaceFieldOptions.account_stage_values]);

  const accountPrimaryUsecseOptions: SelectOptionT[] = useMemo(() => {
    return createOptionsFromValues(
      workspaceFieldOptions.account_primary_usecase_values,
    );
  }, [workspaceFieldOptions.account_primary_usecase_values]);

  const countryOptions: SelectOptionT[] = useMemo(() => {
    return Object.keys(regionMap)
      .sort((a, b) => regionMap[a].country.localeCompare(regionMap[b].country))
      .map((key) => {
        return {
          value: key,
          searchName: regionMap[key].country,
        };
      });
  }, [regionMap]);

  const statusOptions: SelectOptionT[] = useMemo(() => {
    return COMPANY_STATUS_MAP.toArray().map(({ value, label }) => {
      return {
        value,
        searchName: label,
      };
    });
  }, []);

  const agentOptions: SelectOptionT[] = useMemo(() => {
    return Object.keys(agentMap).map((key) => {
      return {
        value: key,
        searchName: agentMap[key].name,
      };
    });
  }, [agentMap]);

  return {
    industryOptions,
    sizeOptions,
    planOptions,
    ratingOptions,
    accountTypeOptions,
    countryOptions,
    statusOptions,
    agentOptions,
    accountStageOptions,
    accountPrimaryUsecseOptions,
  };
};

export default useCompanyAttributes;
